import { createFileRoute, redirect } from '@tanstack/react-router';
import { TabFileError } from '@tabservice/tabfile-client';
import { AppShell } from '@/components/app-shell';
import { SessionError, SessionPending } from '@/components/route-states';
import { hasCustomer, isStaff, meQuery, formsQuery } from '@/lib/queries';
import { signInElsewhere } from '@/lib/session-exit';

/** How long the API gets to confirm the session before the app says it cannot be reached. */
const SESSION_CHECK_TIMEOUT_MS = 15_000;

/**
 * Gate for every signed-in screen.
 *
 * A Firebase session alone is not enough to enter: the API has to confirm who the caller is
 * (`GET /v1/me`). That call is awaited here, before any shell renders. While it runs the
 * person sees a plain "checking your session" screen, never the product. If the API refuses
 * (401 or 403) the Firebase session is signed out and the sign-in page shows the reason. If
 * the API cannot be reached within the timeout, a full-screen error offers retry or sign out.
 */
export const Route = createFileRoute('/_app')({
  pendingComponent: SessionPending,
  errorComponent: SessionError,
  beforeLoad: async ({ context, location, preload }) => {
    const state = await context.auth.ready();
    if (state.status !== 'signed-in') {
      // A hover preload is not a visit: never send the browser anywhere for one.
      if (preload) return;
      // Sign-in lives at the portal. Go straight there rather than through a local page whose
      // only job would be to bounce the browser on a second hop.
      const away = signInElsewhere();
      if (away) {
        window.location.replace(away);
        await new Promise(() => {});
      }
      throw redirect({ to: '/sign-in', search: { redirect: location.href } });
    }
    // Every screen needs the form list for the year picker. Asking for it beside the
    // session check, not after it, takes one round trip off every first paint. A refused
    // session fails the prefetch quietly; the session check below decides what happens.
    void context.queryClient.prefetchQuery(formsQuery());
    let me;
    try {
      me = await withTimeout(context.queryClient.ensureQueryData(meQuery()), SESSION_CHECK_TIMEOUT_MS);
    } catch (error) {
      if (error instanceof TabFileError && (error.status === 401 || error.status === 403)) {
        context.queryClient.clear();
        await context.auth.denySession(error.detail ?? error.title);
        throw redirect({ to: '/sign-in', search: { redirect: undefined } });
      }
      if (error instanceof TimeoutError) {
        throw new Error('Tab Service did not answer. Check your connection and try again.');
      }
      throw error;
    }
    // Staff without a customer of their own have no e-files, uploads or settings to see.
    // Every customer screen sends them to the Tab Service area instead of a refused request.
    if (isStaff(me) && !hasCustomer(me) && !location.pathname.startsWith('/admin')) {
      throw redirect({ to: '/admin' });
    }
  },
  component: AppShell,
});

class TimeoutError extends Error {}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new TimeoutError('timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}
